import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { Button } from "@veorest/ui";
import {
  fetchAdminSettings,
  fetchOeloxFeeBands,
  updateAdminSettings,
  updateOeloxFeeBands,
  type OeloxFeeBand,
} from "@veorest/api-client";
import { Truck, Plus, Trash2, Wallet } from "lucide-react";
import { SettingsCard } from "./SettingsCard";

type BandDraft = {
  min_km: string;
  max_km: string;
  fee_amount: string;
  driver_amount: string;
};

function toDrafts(bands: OeloxFeeBand[]): BandDraft[] {
  return bands.map((b) => ({
    min_km: String(b.min_km),
    max_km: String(b.max_km),
    fee_amount: String(b.fee_amount),
    driver_amount: b.driver_amount == null ? "" : String(b.driver_amount),
  }));
}

/**
 * OELOX global driver delivery fees (Settings → Delivery) — the single pricing
 * source for EVERY trip actually fulfilled by an OELOX global (oelox_network)
 * driver: direct-client fallback AND partner fallback cover trips (the partner
 * earns nothing on cover trips — sign-off 2026-09-20). Partner fleet trips keep
 * the partner's own bands; client fleets keep client rules. No free option:
 * every band fee must be > 0.
 */
export function OeloxDeliveryFeesPanel() {
  const qc = useQueryClient();
  const bandsQuery = useQuery({ queryKey: ["oelox-fee-bands"], queryFn: fetchOeloxFeeBands });
  const [drafts, setDrafts] = useState<BandDraft[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [testKm, setTestKm] = useState(3.5);

  // Global driver commission (consumed by the settlement engine, migration 175).
  const settingsQuery = useQuery({ queryKey: ["admin-settings"], queryFn: fetchAdminSettings });
  const [commission, setCommission] = useState("");
  useEffect(() => {
    const v = settingsQuery.data?.settings?.oelox_global_driver_commission_iqd?.value;
    if (v != null && commission === "") setCommission(String(v));
  }, [settingsQuery.data, commission]);

  const saveCommission = useMutation({
    mutationFn: () => {
      const n = Number(commission);
      if (!Number.isFinite(n) || n < 0 || n > 100000) {
        throw new Error("Commission must be a whole number between 0 and 100,000 IQD.");
      }
      return updateAdminSettings({ oelox_global_driver_commission_iqd: Math.round(n) });
    },
    onSuccess: () => {
      toast.success("Global driver commission saved");
      void qc.invalidateQueries({ queryKey: ["admin-settings"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  useEffect(() => {
    if (!bandsQuery.data || loaded) return;
    setDrafts(toDrafts(bandsQuery.data));
    setLoaded(true);
  }, [bandsQuery.data, loaded]);

  const save = useMutation({
    mutationFn: async () => {
      const parsed: OeloxFeeBand[] = drafts.map((d, index) => {
        const driverRaw = d.driver_amount.trim();
        return {
          min_km: Number(d.min_km),
          max_km: Number(d.max_km),
          fee_amount: Number(d.fee_amount),
          driver_amount: driverRaw === "" ? null : Number(driverRaw),
          sort_order: index,
        };
      });
      if (parsed.length === 0) throw new Error("At least one band is required.");
      for (const b of parsed) {
        if (!Number.isFinite(b.min_km) || !Number.isFinite(b.max_km) || !Number.isFinite(b.fee_amount)) {
          throw new Error("Every band needs numeric min / max / fee.");
        }
        if (b.min_km < 0) throw new Error(`Band starting at ${b.min_km} km: min cannot be negative.`);
        if (b.max_km <= b.min_km) {
          throw new Error(`Band ${b.min_km}–${b.max_km} km: max must be greater than min.`);
        }
        if (b.fee_amount <= 0) {
          throw new Error("Fee must be greater than 0 — OELOX global delivery has no free option.");
        }
        if (b.driver_amount != null) {
          if (!Number.isFinite(b.driver_amount) || b.driver_amount < 0) {
            throw new Error("Driver pay must be a valid amount.");
          }
          if (b.driver_amount > b.fee_amount) {
            throw new Error("Driver pay cannot exceed the customer fee.");
          }
        }
      }
      for (let i = 0; i < parsed.length; i += 1) {
        for (let j = i + 1; j < parsed.length; j += 1) {
          const a = parsed[i];
          const b = parsed[j];
          if (!a || !b) continue;
          if (a.min_km < b.max_km && b.min_km < a.max_km) {
            throw new Error(`Bands ${a.min_km}–${a.max_km} km and ${b.min_km}–${b.max_km} km overlap.`);
          }
        }
      }
      return updateOeloxFeeBands(parsed);
    },
    onSuccess: (bands) => {
      setDrafts(toDrafts(bands));
      toast.success("OELOX delivery fees saved");
      void qc.invalidateQueries({ queryKey: ["oelox-fee-bands"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const calculatorFee = useMemo(() => {
    const parsed = drafts
      .map((d) => {
        const driverRaw = d.driver_amount.trim();
        return {
          min_km: Number(d.min_km),
          max_km: Number(d.max_km),
          fee_amount: Number(d.fee_amount),
          driver_amount:
            driverRaw === "" || !Number.isFinite(Number(driverRaw))
              ? null
              : Number(driverRaw),
        };
      })
      .filter(
        (b) =>
          Number.isFinite(b.min_km) &&
          Number.isFinite(b.max_km) &&
          Number.isFinite(b.fee_amount) &&
          b.max_km > b.min_km
      );
    const hit = parsed.find((b) => testKm >= b.min_km && testKm < b.max_km);
    if (hit) return hit;
    const covered = parsed.filter((b) => b.max_km <= testKm);
    if (covered.length > 0) {
      const top = covered.sort((a, b) => b.max_km - a.max_km)[0];
      if (top) return top;
    }
    return null;
  }, [drafts, testKm]);

  return (
    <>
      <section className="overview-panel mt-4">
        <header className="overview-panel__header">
          <span className="overview-panel__icon" aria-hidden>
            <Truck className="h-4 w-4" />
          </span>
          <div>
            <h3 className="overview-panel__title">OELOX global delivery fees</h3>
            <p className="overview-panel__subtitle">
              Prices every delivery fulfilled by an OELOX global driver — direct-client requests
              and partner fallback cover trips. Partner fleet trips keep the partner's own bands;
              client fleets keep client rules. No free option. Each band sets the customer price
              and the driver pay — the difference (margin) stays with OELOX.
            </p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Button
              type="button"
              variant="ghost"
              icon={<Plus className="h-4 w-4" />}
              onClick={() =>
                setDrafts((prev) => [
                  ...prev,
                  { min_km: "", max_km: "", fee_amount: "", driver_amount: "" },
                ])
              }
            >
              Add band
            </Button>
            <Button
              type="button"
              className="min-h-[40px]"
              loading={save.isPending}
              onClick={() => save.mutate()}
            >
              Save fees
            </Button>
          </div>
        </header>
        <div className="overview-panel__body space-y-3 p-5">
          {bandsQuery.isLoading ? (
            <p className="text-sm text-[var(--text-muted)] font-semibold">Loading bands…</p>
          ) : bandsQuery.isError ? (
            <p className="text-sm font-semibold text-red-500">
              Could not load bands: {(bandsQuery.error as Error).message}
            </p>
          ) : drafts.length === 0 ? (
            <p className="text-sm text-[var(--text-muted)] font-semibold">
              No bands yet — add the first one.
            </p>
          ) : (
            drafts.map((band, index) => (
              <div
                key={index}
                className="flex flex-wrap items-end gap-3 rounded-xl border border-[var(--border)] px-4 py-3"
              >
                <label className="block space-y-1">
                  <span className="text-xs font-bold uppercase text-[var(--text-muted)]">Min km</span>
                  <input
                    className="input-base w-24"
                    inputMode="decimal"
                    value={band.min_km}
                    onChange={(e) =>
                      setDrafts((prev) =>
                        prev.map((b, i) => (i === index ? { ...b, min_km: e.target.value } : b))
                      )
                    }
                  />
                </label>
                <label className="block space-y-1">
                  <span className="text-xs font-bold uppercase text-[var(--text-muted)]">Max km</span>
                  <input
                    className="input-base w-24"
                    inputMode="decimal"
                    value={band.max_km}
                    onChange={(e) =>
                      setDrafts((prev) =>
                        prev.map((b, i) => (i === index ? { ...b, max_km: e.target.value } : b))
                      )
                    }
                  />
                </label>
                <label className="block space-y-1">
                  <span className="text-xs font-bold uppercase text-[var(--text-muted)]">Fee (IQD)</span>
                  <input
                    className="input-base w-28"
                    inputMode="decimal"
                    value={band.fee_amount}
                    onChange={(e) =>
                      setDrafts((prev) =>
                        prev.map((b, i) => (i === index ? { ...b, fee_amount: e.target.value } : b))
                      )
                    }
                  />
                </label>
                <label className="block space-y-1">
                  <span className="text-xs font-bold uppercase text-[var(--text-muted)]">Driver pay (IQD)</span>
                  <input
                    className="input-base w-28"
                    inputMode="decimal"
                    placeholder="—"
                    value={band.driver_amount}
                    onChange={(e) =>
                      setDrafts((prev) =>
                        prev.map((b, i) => (i === index ? { ...b, driver_amount: e.target.value } : b))
                      )
                    }
                  />
                </label>
                <div className="block space-y-1">
                  <span className="text-xs font-bold uppercase text-[var(--text-muted)]">Margin</span>
                  <p className="px-1 py-2 text-sm font-bold">
                    {band.driver_amount.trim() === "" || !Number.isFinite(Number(band.driver_amount))
                      ? "—"
                      : (Number(band.fee_amount) - Number(band.driver_amount)).toLocaleString()}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setDrafts((prev) => prev.filter((_, i) => i !== index))}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))
          )}
        </div>
      </section>

      <section className="overview-panel mt-4">
        <header className="overview-panel__header">
          <span className="overview-panel__icon" aria-hidden>
            <Truck className="h-4 w-4" />
          </span>
          <div>
            <h3 className="overview-panel__title">Fee calculator</h3>
            <p className="overview-panel__subtitle">Slide a distance to preview the OELOX global fee.</p>
          </div>
        </header>
        <div className="overview-panel__body space-y-3 p-5">
          <input
            type="range"
            min={0}
            max={15}
            step={0.1}
            value={testKm}
            onChange={(e) => setTestKm(Number(e.target.value))}
            className="w-full max-w-xl"
          />
          <div className="text-sm font-bold">
            {testKm.toFixed(1)} km →{" "}
            {calculatorFee == null ? (
              <span className="text-[var(--text-muted)]">no covering band</span>
            ) : (
              <span>{calculatorFee.fee_amount.toLocaleString()} IQD</span>
            )}
          </div>
          {calculatorFee?.driver_amount != null ? (
            <div className="text-sm font-bold text-[var(--text-muted)]">
              Driver pay: {calculatorFee.driver_amount.toLocaleString()} IQD · Margin:{" "}
              {(calculatorFee.fee_amount - calculatorFee.driver_amount).toLocaleString()} IQD
            </div>
          ) : null}
        </div>
      </section>

      <SettingsCard
        icon={Wallet}
        title="Global driver commission"
        subtitle="Fallback only — used when the covering band has no driver pay set. Otherwise the band's driver pay applies and margin = fee − driver pay."
        actions={
          <Button
            className="min-h-[40px]"
            loading={saveCommission.isPending}
            onClick={() => saveCommission.mutate()}
          >
            Save commission
          </Button>
        }
        footnote="Consumed by the settlement engine (migration 175). Capped at the trip's delivery fee at settlement time."
      >
        <label className="block space-y-1">
          <span className="text-sm font-bold">Commission per OELOX global trip (IQD)</span>
          <input
            className="input-base max-w-[200px]"
            inputMode="numeric"
            value={commission}
            onChange={(e) => setCommission(e.target.value)}
          />
        </label>
      </SettingsCard>
    </>
  );
}
